// Git Stage Tool - Stages files in the Git repository
// Extends WorkspaceTool - has access to appType, fs, and git
// Adds files to the staging area for commit

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitStageTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitStageTool';
  }
  /**
   * Stage files in the Git repository
   * @param {string|string[]} [filepaths=[]] - File path(s) to stage (use '*' or [] for all)
   * @returns {Promise<Object>} Result with staging status
   */
  async gitStage(filepaths = []) {
    try {
      // Route based on app type
      if (this.appType === "browser") {
        return await this.gitStageForBrowser(filepaths);
      }

      if (this.appType === "local") {
        return await this.gitStageForLocal(filepaths);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitStage`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitstage|error",
          color1: "red",
        },
        "Failed to stage files",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Execute git stage for browser apps using isomorphic-git
   */
  async gitStageForBrowser(filepaths) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    // Check if .git exists
    const gitExists = await this.checkGitExists(fs);
    if (!gitExists) {
      return this.returnError({}, "Git repository not initialized");
    }

    // Get status matrix to determine which files are deletions
    const statusResult = await this.git.statusMatrix({
      fs: this.fs,
      dir,
      filter: (f) => {
        return (
          !f.startsWith(".git/") &&
          f !== ".git" &&
          !f.startsWith(".revolt/") &&
          f !== ".revolt"
        );
      },
    });

    // Create a map of filepath -> status info for quick lookup
    const statusMap = new Map();
    for (const [
      filepath,
      headStatus,
      workdirStatus,
      stageStatus,
    ] of statusResult) {
      if (
        filepath.startsWith(".git/") ||
        filepath === ".git" ||
        filepath.startsWith(".revolt/") ||
        filepath === ".revolt"
      ) {
        continue;
      }
      statusMap.set(filepath, { headStatus, workdirStatus, stageStatus });
    }

    // Normalize filepaths to array and determine which are deletions
    let filesToStage = [];
    const filesToRemove = []; // Deleted files that need git.remove()

    if (
      filepaths === "*" ||
      (Array.isArray(filepaths) && filepaths.length === 0)
    ) {
      // Stage all files - get all unstaged files from status
      // A file needs staging if:
      // 1. It has working directory changes (workdirStatus !== 0)
      // 2. The index doesn't match the working directory (stageStatus !== workdirStatus)
      // This means the changes are unstaged and need to be staged
      for (const [
        filepath,
        headStatus,
        workdirStatus,
        stageStatus,
      ] of statusResult) {
        if (
          filepath.startsWith(".git/") ||
          filepath === ".git" ||
          filepath.startsWith(".revolt/") ||
          filepath === ".revolt"
        ) {
          continue;
        }

        // Determine if file needs staging
        // A file needs staging if the index doesn't match the working directory

        // Case 1: File is deleted in working directory (workdirStatus === 0)
        // but exists in HEAD (headStatus !== 0) and still in index (stageStatus !== 0)
        // This means deletion is not yet staged
        if (workdirStatus === 0 && headStatus !== 0 && stageStatus !== 0) {
          // File exists in HEAD and index but not in working directory - deletion needs staging
          filesToRemove.push(filepath);
        }
        // Case 2: File has changes in working directory that aren't staged
        // workdirStatus === 1 (identical to HEAD) means no changes, skip
        // workdirStatus === 2 (different from HEAD) means changes exist
        // stageStatus === 2 means changes are already staged
        else if (workdirStatus === 2 && stageStatus !== 2) {
          // File is added (headStatus === 0) or modified (headStatus !== 0) but not staged
          filesToStage.push(filepath);
        }
      }

      logger.log(
        {
          tags: "tools|gitstage|info",
          color1: "lime",
        },
        `Found ${filesToStage.length} files to add and ${filesToRemove.length} files to remove from status matrix`,
        {
          filesToStage: filesToStage.slice(0, 10),
          filesToRemove: filesToRemove.slice(0, 10),
          statusMatrixSize: statusResult.length,
        }
      );

      // Also get completely untracked files (not in status matrix at all)
      // These are new files that need to be added
      const allFiles = await this.getAllFiles(dir, fs);
      const trackedFiles = new Set(statusResult.map(([filepath]) => filepath));
      const untrackedFiles = [];
      for (const filepath of allFiles) {
        if (
          !trackedFiles.has(filepath) &&
          !filepath.startsWith(".git/") &&
          filepath !== ".git" &&
          !filepath.startsWith(".revolt/") &&
          filepath !== ".revolt"
        ) {
          untrackedFiles.push(filepath);
          filesToStage.push(filepath); // Untracked files are additions
        }
      }

      logger.log(
        {
          tags: "tools|gitstage|info",
          color1: "lime",
        },
        `Found ${untrackedFiles.length} untracked files to stage`,
        {
          untrackedFiles: untrackedFiles.slice(0, 10),
          totalFilesToStage: filesToStage.length,
        }
      );
    } else {
      // Specific filepaths provided - need to check status of each
      const filepathArray =
        typeof filepaths === "string" ? [filepaths] : filepaths;

      for (const filepath of filepathArray) {
        const status = statusMap.get(filepath);
        if (status) {
          const { headStatus, workdirStatus, stageStatus } = status;
          // Case 1: File is deleted in working directory (workdirStatus === 0)
          // but exists in HEAD (headStatus !== 0) and still in index (stageStatus !== 0)
          if (workdirStatus === 0 && headStatus !== 0 && stageStatus !== 0) {
            // File exists in HEAD and index but not in working directory - deletion needs staging
            filesToRemove.push(filepath);
          }
          // Case 2: File has changes in working directory that aren't staged
          // workdirStatus === 2 (different from HEAD) and stageStatus !== 2 (not staged)
          else if (workdirStatus === 2 && stageStatus !== 2) {
            // File is added or modified but not staged
            filesToStage.push(filepath);
          }
        } else {
          // File not in status matrix - might be untracked (new file)
          // Try to check if it exists
          try {
            await fs.stat(filepath.startsWith("/") ? filepath : `/${filepath}`);
            // File exists - treat as addition
            filesToStage.push(filepath);
          } catch (error) {
            // File doesn't exist - might be a deletion of an untracked file
            // Or it might be a path that was never tracked
            // For now, try to add it (will fail gracefully if needed)
            filesToStage.push(filepath);
          }
        }
      }
    }

    if (filesToStage.length === 0 && filesToRemove.length === 0) {
      return this.returnSuccess(
        {
          stagedCount: 0,
          stagedFiles: [],
        },
        "No files to stage"
      );
    }

    // Stage files: use git.add() for additions/modifications, git.remove() for deletions
    const stagedFiles = [];
    const errors = [];

    // Stage additions and modifications using git.add()
    for (const filepath of filesToStage) {
      try {
        await this.git.add({
          fs: this.fs,
          dir,
          filepath,
        });
        stagedFiles.push(filepath);
      } catch (error) {
        errors.push({ path: filepath, error: error.message });
        logger.warn(
          {
            tags: "tools|gitstage|add",
            color1: "yellow",
          },
          `Failed to stage file: ${filepath}`,
          error
        );
      }
    }

    // Stage deletions using git.remove()
    for (const filepath of filesToRemove) {
      try {
        await this.git.remove({
          fs: this.fs,
          dir,
          filepath,
        });
        stagedFiles.push(filepath);
      } catch (error) {
        errors.push({ path: filepath, error: error.message });
        logger.warn(
          {
            tags: "tools|gitstage|remove",
            color1: "yellow",
          },
          `Failed to stage deletion: ${filepath}`,
          error
        );
      }
    }

    logger.log(
      {
        tags: "tools|gitstage|info",
        color1: "lime",
      },
      `Staged ${stagedFiles.length} files`,
      {
        stagedCount: stagedFiles.length,
        errors: errors.length,
      }
    );

    return this.returnSuccess(
      {
        stagedCount: stagedFiles.length,
        stagedFiles,
        errors: errors.length > 0 ? errors : undefined,
      },
      `Staged ${stagedFiles.length} file(s) successfully`
    );
  }

  /**
   * Stage files via Revolt Desktop IPC (`git add`).
   */
  async gitStageForLocal(filepaths) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitStage) {
      return this.returnError(
        {},
        "Local git stage requires Revolt Desktop (workspaceLocalAppAPI.gitStage)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const stageAll =
      filepaths === "*" ||
      (Array.isArray(filepaths) && filepaths.length === 0);

    const res = await api.gitStage({
      rootPath: root,
      paths: stageAll ? "*" : filepaths,
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to stage files");
    }

    const stagedFiles = res.stagedFiles || [];
    const stagedCount =
      typeof res.stagedCount === "number" ? res.stagedCount : stagedFiles.length;

    logger.log(
      { tags: "tools|gitstage|local|info", color1: "lime" },
      `Staged ${stagedCount} files (local)`
    );

    return this.returnSuccess(
      {
        stagedCount,
        stagedFiles,
        errors: res.errors,
      },
      stagedCount > 0
        ? `Staged ${stagedCount} file(s) successfully`
        : "No files to stage"
    );
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }

  /**
   * Recursively get all files in the filesystem
   */
  async getAllFiles(dir, fs, basePath = "") {
    const files = [];

    try {
      const entries = await fs.readdir(dir === "/" ? "/" : dir);

      for (const entry of entries) {
        if (entry === ".git" || entry === ".revolt") {
          continue;
        }

        const fullPath = dir === "/" ? `/${entry}` : `${dir}/${entry}`;
        const relativePath = basePath ? `${basePath}/${entry}` : entry;

        try {
          const stats = await fs.stat(fullPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            const subFiles = await this.getAllFiles(fullPath, fs, relativePath);
            files.push(...subFiles);
          } else {
            files.push(relativePath);
          }
        } catch (error) {
          // Skip files we can't stat
        }
      }
    } catch (error) {
      // Skip directories we can't read
    }

    return files;
  }
}
